package org.gymly.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CalendarMonth
import androidx.compose.material.icons.outlined.Person
import androidx.compose.material.icons.outlined.Schedule
import androidx.compose.material.icons.outlined.Wallet
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.blur
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.gymly.model.MarketingItem
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlyFont
import org.gymly.theme.GymlyRadius
import org.gymly.theme.GymlySpace
import org.gymly.util.downloadAuthenticatedFile
import org.gymly.util.openBytes

@Composable
fun HomeTile(
    label: String,
    imageUrl: String,
    icon: ImageVector,
    width: Dp,
    onClick: () -> Unit,
) {
    Card(
        modifier = Modifier
            .width(width)
            .height(120.dp)
            .clickable(onClick = onClick),
        shape = RoundedCornerShape(GymlyRadius.card),
        colors = CardDefaults.cardColors(containerColor = GymlyColors.SurfaceContainer),
        border = CardDefaults.outlinedCardBorder(true),
    ) {
        Box(Modifier.fillMaxSize()) {
            AsyncImage(
                model = imageUrl,
                contentDescription = label,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop,
            )
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Brush.verticalGradient(listOf(Color.Transparent, Color.Black.copy(0.55f)))),
                contentAlignment = Alignment.BottomStart,
            ) {
                Column(Modifier.padding(GymlySpace.sm)) {
                    Icon(icon, contentDescription = null, tint = Color.White)
                    Text(label, color = Color.White, fontWeight = FontWeight.Bold, fontSize = GymlyFont.small)
                }
            }
        }
    }
}

private const val MARKETING_SLIDE_MS = 5_000L

@Composable
fun MarketingCarousel(items: List<MarketingItem>, pageWidth: Dp) {
    val slides = items.flatMap { promo ->
        val videoUrl = promo.videoUrl?.takeIf { it.isNotBlank() }
        val images = promo.slides.sortedBy { it.sortIndex }
            .filter { slide -> videoUrl == null || slide.fullUrl != videoUrl }
            .map { slide ->
                Triple("${promo.id}-${slide.id}", promo.title, promo.details to slide.fullUrl)
            }
        val video = videoUrl?.let { url ->
            listOf(Triple("${promo.id}-video", promo.title, promo.details to url))
        }.orEmpty()
        images + video
    }

    if (slides.isEmpty()) {
        Text(
            "No hay campañas de marketing activas.",
            color = GymlyColors.TextMuted,
            modifier = Modifier.padding(GymlySpace.md),
        )
        return
    }

    // Varias piezas: pasan solas cada pocos segundos; el usuario también puede deslizar.
    val pagerState = rememberPagerState(pageCount = { slides.size })
    LaunchedEffect(slides.size) {
        if (slides.size > 1) {
            while (true) {
                delay(MARKETING_SLIDE_MS)
                pagerState.animateScrollToPage((pagerState.currentPage + 1) % slides.size)
            }
        }
    }

    // Móvil: casi todo el ancho. Escritorio: tarjeta de ancho máximo, centrada, para no estirar el banner.
    val cardWidth = minOf(pageWidth - GymlySpace.md * 2, 420.dp)

    Column(Modifier.padding(top = GymlySpace.md)) {
        Text(
            "Promociones",
            fontWeight = FontWeight.Bold,
            fontSize = GymlyFont.body,
            modifier = Modifier.padding(horizontal = GymlySpace.md, vertical = GymlySpace.sm),
        )
        // Cada página ocupa todo el ancho: se ve una sola tarjeta a la vez y la siguiente entra al pasar.
        HorizontalPager(
            state = pagerState,
            modifier = Modifier.fillMaxWidth(),
        ) { page ->
            val (key, title, detailsAndUrl) = slides[page]
            Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                MarketingSlideCard(
                    key = key,
                    title = title,
                    details = detailsAndUrl.first,
                    imageUrl = detailsAndUrl.second,
                    modifier = Modifier.width(cardWidth),
                )
            }
        }
        if (slides.size > 1) {
            Row(
                Modifier.fillMaxWidth().padding(top = GymlySpace.sm),
                horizontalArrangement = Arrangement.Center,
            ) {
                slides.indices.forEach { i ->
                    Box(
                        Modifier
                            .padding(horizontal = 3.dp)
                            .size(8.dp)
                            .clip(CircleShape)
                            .background(if (i == pagerState.currentPage) GymlyColors.Primary else GymlyColors.Outline),
                    )
                }
            }
        }
    }
}

@Composable
private fun MarketingSlideCard(
    key: String,
    title: String,
    details: String?,
    imageUrl: String,
    modifier: Modifier,
) {
    val isVideo = key.endsWith("-video")
    val uriHandler = androidx.compose.ui.platform.LocalUriHandler.current
    val scope = rememberCoroutineScope()
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(GymlyRadius.card),
    ) {
        Column {
            if (isVideo) {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .height(200.dp)
                        .background(GymlyColors.Primary)
                        .clickable {
                            val hosted = imageUrl.contains("/api/marketing/promotions/")
                            if (!hosted) {
                                runCatching { uriHandler.openUri(imageUrl) }
                                return@clickable
                            }
                            scope.launch {
                                runCatching {
                                    val file = downloadAuthenticatedFile(imageUrl, "video")
                                    openBytes(file.mimeType, file.bytes)
                                }
                            }
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    Text("Ver video", color = GymlyColors.OnPrimary, fontWeight = FontWeight.Bold)
                }
            } else {
                // Marco 4:5 (1080×1350): la imagen se ve completa. Si no es 4:5 (p. ej. banners anteriores),
                // el hueco se llena con una copia borrosa de la misma imagen; en 4:5 esa copia queda tapada.
                Box(
                    Modifier
                        .fillMaxWidth()
                        .aspectRatio(0.8f),
                ) {
                    AsyncImage(
                        model = imageUrl,
                        contentDescription = null,
                        modifier = Modifier.matchParentSize().blur(24.dp),
                        contentScale = ContentScale.Crop,
                    )
                    AsyncImage(
                        model = imageUrl,
                        contentDescription = title,
                        modifier = Modifier.matchParentSize(),
                        contentScale = ContentScale.Fit,
                    )
                }
            }
            Column(Modifier.padding(GymlySpace.md)) {
                Text(title, fontWeight = FontWeight.Bold, maxLines = 2)
                if (!details.isNullOrBlank()) {
                    Text(details, color = GymlyColors.TextMuted, modifier = Modifier.padding(top = GymlySpace.xs), maxLines = 3)
                }
            }
        }
    }
}

fun homeTileIcon(label: String): ImageVector = when (label) {
    "Perfil" -> Icons.Outlined.Person
    "Pagos" -> Icons.Outlined.Wallet
    "Reservas" -> Icons.Outlined.CalendarMonth
    else -> Icons.Outlined.Schedule
}

@Composable
fun SummaryCard(
    title: String,
    modifier: Modifier = Modifier,
    cardHeight: Dp? = 160.dp,
    content: @Composable () -> Unit,
) {
    val accent = GymlyColors.Primary
    Card(
        modifier = if (cardHeight != null) modifier.height(cardHeight) else modifier,
        shape = RoundedCornerShape(GymlyRadius.card),
        colors = CardDefaults.cardColors(containerColor = accent.copy(alpha = 0.07f)),
        border = CardDefaults.outlinedCardBorder(true),
    ) {
        Column(Modifier.padding(GymlySpace.md).fillMaxSize()) {
            Row(
                Modifier.padding(bottom = GymlySpace.sm),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    Modifier
                        .width(28.dp)
                        .height(28.dp)
                        .clip(RoundedCornerShape(50))
                        .background(accent.copy(alpha = 0.16f)),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(summaryCardIcon(title), contentDescription = null, tint = accent, modifier = Modifier.height(16.dp))
                }
                Text(title, fontWeight = FontWeight.Bold, modifier = Modifier.padding(start = 8.dp))
            }
            content()
        }
    }
}

private fun summaryCardIcon(title: String): ImageVector = when (title) {
    "Últimos pagos" -> Icons.Outlined.Wallet
    "Horario de hoy" -> Icons.Outlined.Schedule
    else -> Icons.Outlined.CalendarMonth
}

@Composable
fun PortalListCard(
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    Card(
        modifier = modifier
            .fillMaxWidth()
            .then(if (onClick != null) Modifier.clickable(onClick = onClick) else Modifier)
            .padding(bottom = GymlySpace.sm),
        shape = RoundedCornerShape(GymlyRadius.card),
        colors = CardDefaults.cardColors(containerColor = GymlyColors.SurfaceContainer),
        border = CardDefaults.outlinedCardBorder(true),
    ) {
        Column(Modifier.padding(GymlySpace.md)) {
            content()
        }
    }
}

@Composable
fun TwoButtonRow(
    cancelText: String,
    confirmText: String,
    onCancel: () -> Unit,
    onConfirm: () -> Unit,
    loading: Boolean = false,
) {
    Row(horizontalArrangement = Arrangement.spacedBy(GymlySpace.md)) {
        GymlySecondaryButton(text = cancelText, onClick = onCancel, modifier = Modifier.weight(1f))
        GymlyPrimaryButton(text = confirmText, onClick = onConfirm, modifier = Modifier.weight(1f), loading = loading)
    }
}
